import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "新番速递", to: "/category/new" },
  { label: "剧场版", to: "/category/movie" },
  { label: "OVA专栏", to: "/category/ova" },
  { label: "热门排行", to: "/ranking" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/ranking?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/90 shadow-[0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-secondary-400 to-primary-500 text-background-50">
            <i className="ri-bubble-chart-fill text-xl" />
          </span>
          <span className="font-heading text-xl text-foreground-950">
            漫番志<span className="text-primary-400">.</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => {
            const active =
              link.to === "/"
                ? location.pathname === "/"
                : link.to !== "/" && location.pathname.startsWith(link.to.split("?")[0]);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 font-bold text-background-50"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`items-center overflow-hidden rounded-full bg-background-100 ring-1 ring-background-300/70 transition-all duration-300 ${
              searchOpen ? "flex w-44 md:w-56" : "hidden w-0"
            }`}
          >
            <i className="ri-search-line pl-3 text-sm text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索作品 / 类型"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            />
          </form>
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
          >
            <i className={`text-lg ${searchOpen ? "ri-close-line" : "ri-search-line"}`} />
          </button>

          <Link
            to="/ranking"
            className="hidden whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-2 text-sm font-bold text-background-50 transition-opacity hover:opacity-90 sm:inline-flex"
          >
            立即追番
          </Link>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="mx-4 mb-3 rounded-2xl bg-background-100 p-3 ring-1 ring-background-300/60 lg:hidden">
          <div className="grid gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-lg px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-200"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}